
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { DemographicData, DemographicSegment } from '@/components/polls/demographics/types';

interface UsePollDemographicsProps {
  pollId: string;
}

export const usePollDemographics = ({ pollId }: UsePollDemographicsProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [demographicData, setDemographicData] = useState<DemographicData[]>([]);

  const fetchDemographicData = useCallback(async () => {
    if (!pollId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Get poll data to understand poll structure and sentiment values
      const { data: poll, error: pollError } = await supabase
        .from('polls')
        .select('questions')
        .eq('id', pollId)
        .single();
        
      if (pollError) throw pollError;
      
      // Safely parse poll questions and options
      const pollQuestions = typeof poll.questions === 'string' 
        ? JSON.parse(poll.questions) 
        : poll.questions;
      
      // Get all options with their sentiment values
      const pollOptions = pollQuestions?.options || [];
      
      // Create a map of option ID to sentiment value
      const optionSentiments = new Map();
      pollOptions.forEach((option: any) => {
        // Use the explicitly defined sentiment value or fall back to position-based default
        let sentiment = 0;
        if (option.sentiment !== undefined) {
          sentiment = option.sentiment;
        } else {
          // Position-based fallback - first option typically agrees (100), second disagrees (-100)
          const index = pollOptions.findIndex((o: any) => o.id === option.id);
          if (index === 0) sentiment = 100;      // First option (typically agree)
          else if (index === 1) sentiment = -100; // Second option (typically disagree)
        }
        
        optionSentiments.set(option.id, sentiment);
      });
      
      // Get all votes for this poll
      const { data: votes, error: votesError } = await supabase
        .from('poll_votes')
        .select(`
          user_id,
          option_id
        `)
        .eq('poll_id', pollId);
        
      if (votesError) throw votesError;
      
      if (!votes || votes.length === 0) {
        setDemographicData([]);
        setIsLoading(false);
        return;
      }
      
      // Get user demographics for those who voted
      const userIds = votes.map(vote => vote.user_id).filter(Boolean);
      
      if (userIds.length === 0) {
        setDemographicData([]);
        setIsLoading(false);
        return;
      }
      
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, demographics')
        .in('id', userIds);
        
      if (profilesError) throw profilesError;
      
      // Process demographics data with weighted sentiment calculations
      const demographicResults = processProfileDemographics(profiles, votes, optionSentiments);
      setDemographicData(demographicResults);
    } catch (fetchError) {
      console.error('Error fetching poll demographics:', fetchError);
      setError(fetchError as Error);
    } finally {
      setIsLoading(false);
    }
  }, [pollId]);
  
  // Process profile demographics into structured data for visualization
  const processProfileDemographics = (profiles: any[], votes: any[], optionSentiments: Map<number, number>) => {
    const result: DemographicData[] = [];
    
    // Only process if we have profiles with demographics
    if (!profiles || profiles.length === 0) return result;
    
    // Create a map of userId to their vote option
    const voteMap: Record<string, number> = {};
    votes.forEach(vote => {
      if (vote.user_id) {
        voteMap[vote.user_id] = vote.option_id;
      }
    });
    
    // Demographics we want to analyze - put age_group first
    const demographicCategories = ['age_group', 'gender', 'education_level', 'residence_type', 'political_leaning'];
    
    demographicCategories.forEach(category => {
      const segments: Record<string, { count: number, weightedSentiment: number, responses: Record<number, number> }> = {};
      let validCount = 0;
      
      // Count occurrences of each demographic segment with weighted sentiment
      profiles.forEach(profile => {
        if (profile.demographics && profile.demographics[category]) {
          const value = profile.demographics[category];
          validCount++;
          
          if (!segments[value]) {
            segments[value] = { 
              count: 0, 
              weightedSentiment: 0,
              responses: {} // Track responses by option ID
            };
          }
          
          segments[value].count++;
          
          // Get this user's vote and its sentiment value
          const optionId = voteMap[profile.id];
          if (optionId !== undefined) {
            // Track responses by option
            segments[value].responses[optionId] = (segments[value].responses[optionId] || 0) + 1;
            
            // Add weighted sentiment value
            const sentimentValue = optionSentiments.get(optionId) || 0;
            segments[value].weightedSentiment += sentimentValue;
          }
        }
      });
      
      // If we have valid data for this category, add it to the result
      if (validCount > 0) {
        const segmentArray: DemographicSegment[] = Object.entries(segments).map(([name, data]) => {
          // Calculate agreement percentage based on weighted sentiment
          const sentimentScore = data.count > 0 ? data.weightedSentiment / data.count : 0;
          // Normalize to 0-100 range (assuming sentiment is between -100 and 100)
          const agreePercentage = Math.round(((sentimentScore + 100) / 200) * 100);
          
          return {
            name,
            count: data.count,
            percentage: Math.round((data.count / validCount) * 100),
            agreePercentage,
            // Include distribution data for advanced visualizations
            optionDistribution: data.responses
          };
        });
        
        // Only add categories that have data
        if (segmentArray.length > 0) {
          // Format category names to be more readable
          const formatCategory = (cat: string) => {
            return cat
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
          };
          
          result.push({
            category: formatCategory(category),
            segments: segmentArray
          });
        }
      }
    });
    
    return result;
  };
  
  // Fetch demographic data on mount and when pollId changes
  useEffect(() => {
    fetchDemographicData();
  }, [fetchDemographicData]);
  
  return {
    demographicData,
    isLoading,
    error,
    refreshDemographics: fetchDemographicData
  };
};
